import { authService } from '../services/api';
import notificationService from '../services/notificationService';
import { getUser, getActiveRole } from './sessionKeys';

/**
 * Comprehensive logout utility that clears all session data
 * Works for all user types: user, admin, astrologer
 */
export const performLogout = async (navigate) => {
  try {
    // 1. Call backend logout endpoint (if it exists)
    try {
      await authService.logout();
    } catch (error) {
      // Backend logout might fail if endpoint doesn't exist or token expired
      // Continue with client-side cleanup anyway
      console.warn('Backend logout failed, continuing with client-side cleanup:', error.message);
    }

    // 1b. Deactivate FCM token (fire-and-forget — don't block logout)
    try {
      const fcmToken = localStorage.getItem('fcm_device_token');
      if (fcmToken) {
        notificationService.unregisterDeviceToken(fcmToken).catch(() => {});
      }
    } catch (e) { /* silent */ }

    // 2. Clear localStorage (token, user data, any cached data)
    localStorage.clear();

    // 3. Clear sessionStorage
    sessionStorage.clear();

    // 4. Clear all cookies
    clearAllCookies();

    // 5. Clear any Redux state (if using Redux)
    // This will be handled by the navigation/page reload

    // 6. Disconnect Socket.IO if connected
    disconnectSocket();

    // 7. Clear browser cache for this domain (best effort)
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
      });
    }

    // 8. Navigate to login page — full page reload to reset all React state (AuthContext, Redux, etc.)
    window.location.replace('/login');

    console.log('Logout completed successfully');
  } catch (error) {
    console.error('Error during logout:', error);
    window.location.replace('/login');
  }
};

/**
 * Clear all cookies for the current domain
 */
const clearAllCookies = () => {
  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i];
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();

    // Clear cookie for current domain
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;

    // Also try to clear for parent domains
    const domain = window.location.hostname;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${domain}`;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${domain}`;
  }
};

/**
 * Disconnect Socket.IO connection if exists
 */
const disconnectSocket = () => {
  try {
    // Check if socket instance exists in window (some apps store it globally)
    if (window.socket) {
      window.socket.disconnect();
      window.socket = null;
    }

    // Also check for socket.io client manager
    if (window.io && window.io.sockets) {
      Object.values(window.io.sockets).forEach(socket => {
        if (socket.disconnect) {
          socket.disconnect();
        }
      });
    }
  } catch (error) {
    console.warn('Error disconnecting socket:', error);
  }
};

/**
 * Check if user is authenticated (role-scoped, falls back to legacy keys)
 */
export const isAuthenticated = () => {
  const role = getActiveRole();
  const user = getUser(role);
  return !!user;
};

/**
 * Get current user data (role-scoped, falls back to legacy keys)
 */
export const getCurrentUser = () => {
  try {
    const role = getActiveRole();
    const user = getUser(role);
    return user;
  } catch (error) {
    console.error('Error parsing user data:', error);
    return null;
  }
};

/**
 * Get current user role
 */
export const getUserRole = () => {
  const user = getCurrentUser();
  return user?.role || null;
};
